import { useState, useEffect, useRef, useCallback } from 'react'
import { Message } from '../../../hooks/useMessages'
import { useAuth } from '../../../hooks/useAuth'
import { Conversation } from '../../../hooks/useMessages'
import SharedContentCard, { decodeSharedContent, SharedContentData } from '../../../components/base/SharedContentCard'
import ShareToMessageModal, { SharedContent } from '../../../components/base/ShareToMessageModal'
import { supabase } from '../../../lib/supabase'
import { encodeSharedContent } from '../../../components/base/SharedContentCard'
import GroupSettingsModal from './GroupSettingsModal'
import InviteMembersModal from './InviteMembersModal'

interface ChatAreaProps {
  conversation: Conversation | null
  messages: Message[]
  messagesLoading: boolean
  onSendMessage: (content: string) => Promise<void>
  onBack: () => void
  onDeleteConversation: () => void
  onDeleteMessage?: (messageId: string) => Promise<void>
  onLeaveGroup?: () => void
  onViewGroupInfo?: () => void
  onRemoveMember?: (userId: string) => Promise<void>
  onUpdateGroupSettings?: (name: string, icon: string) => Promise<void>
  onInviteMembers?: (userIds: string[]) => Promise<void>
}

interface MessageReaction {
  emoji: string
  users: string[]
}

interface MessageReactionsMap {
  [messageId: string]: MessageReaction[]
}

interface MessageRepliesMap {
  [messageId: string]: {
    id: string
    content: string
    sender_name: string
  }
}

interface PinnedMessage {
  id: string
  content: string
  sender_name: string
  sender_id: string
  pinned_at: string
  pinned_by: string
}

const MESSAGE_REACTIONS = [
  { emoji: '👍', name: 'like' },
  { emoji: '❤️', name: 'love' },
  { emoji: '😂', name: 'laugh' },
  { emoji: '😮', name: 'wow' },
  { emoji: '😢', name: 'sad' },
  { emoji: '🙏', name: 'pray' }
]

const EMOJI_CATEGORIES = [
  {
    name: 'Smileys',
    icon: 'ri-emotion-line',
    emojis: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮‍💨', '🤥']
  },
  {
    name: 'Gestures',
    icon: 'ri-hand-heart-line',
    emojis: ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '💪', '🦾', '❤️', '🧡', '💛', '💚', '💙', '💜']
  },
  {
    name: 'Fun',
    icon: 'ri-star-smile-line',
    emojis: ['🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '⭐', '🌟', '✨', '💫', '🔥', '💥', '💯', '🎯', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎵', '🎶', '💃', '🕺', '🥳', '🤩', '😎', '🤓', '🧐', '🥸', '🤡', '👻', '💀', '☠️', '👽', '🤖', '🎃', '😈', '👿']
  }
]

const EmojiPicker = ({ onSelect, onClose }: { onSelect: (emoji: string) => void; onClose: () => void }) => {
  const [activeCategory, setActiveCategory] = useState(0)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full left-0 mb-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50 animate-fadeIn"
    >
      <div className="flex items-center gap-1 px-2 py-2 border-b border-gray-100 bg-gray-50">
        {EMOJI_CATEGORIES.map((cat, idx) => (
          <button
            key={cat.name}
            onClick={() => setActiveCategory(idx)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              activeCategory === idx
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                : 'text-gray-500 hover:bg-gray-200'
            }`}
            title={cat.name}
          >
            <i className={`${cat.icon} text-lg`}></i>
          </button>
        ))}
      </div>
      <div className="p-2 h-48 overflow-y-auto">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2 px-1">
          {EMOJI_CATEGORIES[activeCategory].name}
        </p>
        <div className="grid grid-cols-8 gap-0.5">
          {EMOJI_CATEGORIES[activeCategory].emojis.map((emoji, idx) => (
            <button
              key={idx}
              onClick={() => onSelect(emoji)}
              className="w-8 h-8 flex items-center justify-center text-xl hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

const ReactionPicker = ({ onSelect }: { onSelect: (emoji: string) => void }) => {
  return (
    <div className="flex items-center gap-0.5 bg-white rounded-full shadow-lg border border-gray-200 px-2 py-1.5 z-50 animate-fadeIn">
      {MESSAGE_REACTIONS.map((reaction) => (
        <button
          key={reaction.name}
          onClick={(e) => {
            e.stopPropagation()
            onSelect(reaction.emoji)
          }}
          className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded-full transition-all hover:scale-125 cursor-pointer"
          title={reaction.name}
        >
          {reaction.emoji}
        </button>
      ))}
    </div>
  )
}

const MessageReactionsDisplay = ({ 
  reactions, 
  isOwn,
  onReactionClick 
}: { 
  reactions: MessageReaction[]
  isOwn: boolean
  onReactionClick: (emoji: string) => void
}) => {
  if (reactions.length === 0) return null

  return (
    <div className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className="flex items-center gap-0.5 bg-white rounded-full shadow-sm border border-gray-200 px-1.5 py-0.5">
        {reactions.map((reaction) => (
          <button
            key={reaction.emoji}
            onClick={() => onReactionClick(reaction.emoji)}
            className="flex items-center gap-0.5 hover:bg-gray-100 rounded-full px-1 py-0.5 transition-colors cursor-pointer"
          >
            <span className="text-sm">{reaction.emoji}</span>
            {reaction.users.length > 1 && (
              <span className="text-[10px] text-gray-500 font-medium">{reaction.users.length}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}

const QuotedMessage = ({ 
  content, 
  senderName, 
  isOwn,
  onClick
}: { 
  content: string
  senderName: string
  isOwn: boolean
  onClick?: () => void
}) => {
  const truncatedContent = content.length > 100 ? content.slice(0, 100) + '...' : content
  
  return (
    <div 
      onClick={onClick}
      className={`flex items-start gap-2 px-3 py-2 rounded-lg mb-1 cursor-pointer transition-colors ${
        isOwn 
          ? 'bg-purple-100/50' 
          : 'bg-gray-100'
      }`}
    >
      <div className={`w-1 self-stretch rounded-full flex-shrink-0 ${
        isOwn ? 'bg-purple-500' : 'bg-pink-500'
      }`}></div>
      <div className="min-w-0 flex-1">
        <p className={`text-[11px] font-semibold mb-0.5 ${
          isOwn ? 'text-purple-600' : 'text-pink-600'
        }`}>
          {senderName}
        </p>
        <p className="text-xs leading-relaxed line-clamp-2 text-gray-600">
          {truncatedContent}
        </p>
      </div>
    </div>
  )
}

const ReplyPreview = ({ 
  message, 
  senderName,
  onCancel 
}: { 
  message: Message
  senderName: string
  onCancel: () => void 
}) => {
  const decoded = decodeSharedContent(message.content)
  const displayContent = decoded.isShared ? (decoded.text || 'Shared content') : message.content
  const truncatedContent = displayContent.length > 80 ? displayContent.slice(0, 80) + '...' : displayContent

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-purple-50 border-l-4 border-purple-500 rounded-r-lg mb-2 animate-fadeIn">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-purple-600 mb-0.5">
          Replying to {senderName}
        </p>
        <p className="text-sm text-gray-600 truncate">{truncatedContent}</p>
      </div>
      <button
        onClick={onCancel}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-purple-100 transition-colors cursor-pointer flex-shrink-0"
      >
        <i className="ri-close-line text-gray-500"></i>
      </button>
    </div>
  )
}

const ChatArea = ({
  conversation,
  messages,
  messagesLoading,
  onSendMessage,
  onBack,
  onDeleteConversation,
  onDeleteMessage,
  onLeaveGroup,
  onRemoveMember,
  onUpdateGroupSettings,
  onInviteMembers
}: ChatAreaProps) => {
  const { user } = useAuth()
  const [newMessage, setNewMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [messageReactions, setMessageReactions] = useState<MessageReactionsMap>({})
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null)
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null)
  const [replyingTo, setReplyingTo] = useState<Message | null>(null)
  const [messageReplies, setMessageReplies] = useState<MessageRepliesMap>({})
  const [forwardContent, setForwardContent] = useState<SharedContent | null>(null)
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [showGroupMembers, setShowGroupMembers] = useState(false)
  const [showGroupSettings, setShowGroupSettings] = useState(false)
  const [showInviteMembers, setShowInviteMembers] = useState(false)
  const [removingMember, setRemovingMember] = useState<string | null>(null)
  const [pinnedMessages, setPinnedMessages] = useState<PinnedMessage[]>([])
  const [showPinnedMessages, setShowPinnedMessages] = useState(false)
  const [showMessageMenu, setShowMessageMenu] = useState<string | null>(null)
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)

  const isCurrentUserAdmin = conversation?.is_group 
    ? conversation.participants.find(p => p.user_id === user?.id)?.is_admin 
    : false

  const getSenderName = useCallback((senderId: string) => {
    if (senderId === user?.id) return 'You'
    if (conversation?.is_group) {
      const sender = conversation.participants.find(p => p.user_id === senderId)
      return sender?.user_profile?.username || 'Unknown'
    }
    const other = conversation?.participants.find(p => p.user_id !== user?.id)
    return other?.user_profile?.username || 'Unknown'
  }, [user?.id, conversation])

  const getSenderAvatar = useCallback((senderId: string) => {
    if (senderId === user?.id) return null
    const sender = conversation?.participants.find(p => p.user_id === senderId)
    return sender?.user_profile?.avatar_url || null
  }, [user?.id, conversation])

  const handleReply = useCallback((message: Message) => {
    setReplyingTo(message)
    inputRef.current?.focus()
  }, [])

  const cancelReply = useCallback(() => {
    setReplyingTo(null)
  }, [])

  const scrollToMessage = useCallback((messageId: string) => {
    const element = document.getElementById(`message-${messageId}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      element.classList.add('bg-purple-50')
      setTimeout(() => {
        element.classList.remove('bg-purple-50')
      }, 2000)
    }
  }, [])

  const handleReaction = useCallback((messageId: string, emoji: string) => {
    const currentUserId = user?.id || 'current-user'
    
    setMessageReactions(prev => {
      const messageReacts = prev[messageId] || []
      const existingReaction = messageReacts.find(r => r.emoji === emoji)
      
      if (existingReaction) {
        if (existingReaction.users.includes(currentUserId)) {
          const updatedUsers = existingReaction.users.filter(u => u !== currentUserId)
          if (updatedUsers.length === 0) {
            return {
              ...prev,
              [messageId]: messageReacts.filter(r => r.emoji !== emoji)
            }
          }
          return {
            ...prev,
            [messageId]: messageReacts.map(r => 
              r.emoji === emoji ? { ...r, users: updatedUsers } : r
            )
          }
        } else {
          return {
            ...prev,
            [messageId]: messageReacts.map(r => 
              r.emoji === emoji ? { ...r, users: [...r.users, currentUserId] } : r
            )
          }
        }
      } else {
        return {
          ...prev,
          [messageId]: [...messageReacts, { emoji, users: [currentUserId] }]
        }
      }
    })
    
    setShowReactionPicker(null)
  }, [user?.id])

  const handleForward = useCallback((content: SharedContentData) => {
    setForwardContent(content as SharedContent)
    setShowForwardModal(true)
  }, [])

  const handleForwardToConversation = useCallback(async (conversationId: string, content: SharedContent) => {
    const encodedContent = encodeSharedContent(content as SharedContentData)
    
    await supabase.from('messages').insert({
      conversation_id: conversationId,
      sender_id: user?.id,
      content: encodedContent
    })

    await supabase
      .from('conversations')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', conversationId)
  }, [user?.id])

  const handleForwardToNewConversation = useCallback(async (userId: string, content: SharedContent) => {
    if (!user) return

    const { data: existingParticipants } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', user.id)

    if (existingParticipants && existingParticipants.length > 0) {
      const convIds = existingParticipants.map(p => p.conversation_id)
      
      const { data: otherParticipants } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', userId)
        .in('conversation_id', convIds)

      if (otherParticipants && otherParticipants.length > 0) {
        await handleForwardToConversation(otherParticipants[0].conversation_id, content)
        return
      }
    }

    const { data: newConv } = await supabase
      .from('conversations')
      .insert({ created_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .select()
      .single()

    if (newConv) {
      await supabase.from('conversation_participants').insert([
        { conversation_id: newConv.id, user_id: user.id },
        { conversation_id: newConv.id, user_id: userId }
      ])

      const encodedContent = encodeSharedContent(content as SharedContentData)
      await supabase.from('messages').insert({
        conversation_id: newConv.id,
        sender_id: user.id,
        content: encodedContent
      })
    }
  }, [user, handleForwardToConversation])

  const handlePinMessage = useCallback((message: Message) => {
    const senderName = getSenderName(message.sender_id)
    const newPinnedMessage: PinnedMessage = {
      id: message.id,
      content: message.content,
      sender_name: senderName,
      sender_id: message.sender_id,
      pinned_at: new Date().toISOString(),
      pinned_by: user?.id || ''
    }
    setPinnedMessages(prev => {
      if (prev.some(p => p.id === message.id)) {
        return prev
      }
      if (prev.length >= 5) {
        return [newPinnedMessage, ...prev.slice(0, 4)]
      }
      return [newPinnedMessage, ...prev]
    })
  }, [getSenderName, user?.id])

  const handleUnpinMessage = useCallback((messageId: string) => {
    setPinnedMessages(prev => prev.filter(p => p.id !== messageId))
  }, [])

  const isMessagePinned = useCallback((messageId: string) => {
    return pinnedMessages.some(p => p.id === messageId)
  }, [pinnedMessages])

  const handleDeleteMessage = useCallback(async (messageId: string) => {
    if (onDeleteMessage) {
      setDeletingMessageId(messageId)
      try {
        await onDeleteMessage(messageId)
      } finally {
        setDeletingMessageId(null)
        setShowMessageMenu(null)
      }
    }
  }, [onDeleteMessage])

  const scrollToBottom = (behavior: 'auto' | 'smooth' = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    scrollToBottom('auto')
  }, [conversation?.id])

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  useEffect(() => {
    if (conversation) {
      inputRef.current?.focus()
    }
    setReplyingTo(null)
    setShowGroupMembers(false)
    setShowPinnedMessages(false)
    setPinnedMessages([])
  }, [conversation?.id])

  useEffect(() => {
    const handleClickOutside = () => {
      setShowReactionPicker(null)
    }
    if (showReactionPicker) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [showReactionPicker])

  const getOtherParticipant = () => {
    if (!conversation) return null
    return conversation.participants.find(p => p.user_id !== user?.id)
  }

  const other = getOtherParticipant()
  const isGroup = conversation?.is_group

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || sending) return
    setSending(true)
    
    const replyInfo = replyingTo ? {
      id: replyingTo.id,
      content: replyingTo.content,
      sender_name: getSenderName(replyingTo.sender_id)
    } : null
    
    try {
      await onSendMessage(newMessage.trim())
      
      if (replyInfo && messages.length > 0) {
        setTimeout(() => {
          const latestMessages = document.querySelectorAll('[data-message-id]')
          if (latestMessages.length > 0) {
            const lastMessageId = latestMessages[latestMessages.length - 1].getAttribute('data-message-id')
            if (lastMessageId) {
              setMessageReplies(prev => ({
                ...prev,
                [lastMessageId]: replyInfo
              }))
            }
          }
        }, 100)
      }
      
      setNewMessage('')
      setReplyingTo(null)
      inputRef.current?.focus()
    } catch {
      // error handled upstream
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
    if (e.key === 'Escape' && replyingTo) {
      cancelReply()
    }
  }

  const handleEmojiSelect = (emoji: string) => {
    const textarea = inputRef.current
    if (textarea) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newValue = newMessage.slice(0, start) + emoji + newMessage.slice(end)
      setNewMessage(newValue)
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + emoji.length, start + emoji.length)
      }, 0)
    } else {
      setNewMessage(prev => prev + emoji)
    }
    setShowEmojiPicker(false)
  }

  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDateSeparator = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / 86400000)

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' })
    return date.toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })
  }

  const shouldShowDateSeparator = (index: number) => {
    if (index === 0) return true
    const current = new Date(messages[index].created_at).toDateString()
    const previous = new Date(messages[index - 1].created_at).toDateString()
    return current !== previous
  }

  const shouldShowSenderInfo = (index: number) => {
    if (!isGroup) return false
    if (index === 0) return true
    const current = messages[index]
    const previous = messages[index - 1]
    if (current.sender_id === user?.id) return false
    if (current.sender_id !== previous.sender_id) return true
    if (shouldShowDateSeparator(index)) return true
    return false
  }

  // Empty state
  if (!conversation) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-amber-50/30">
        <div className="text-center max-w-md px-8">
          <div className="w-32 h-32 mx-auto mb-6 relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                <i className="ri-message-3-line text-5xl text-purple-500"></i>
              </div>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">AI Drama Community</h2>
          <p className="text-sm text-gray-500 leading-relaxed">
            Send and receive messages with community members. Start a conversation by selecting a chat or creating a new one.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-gray-400">
            <i className="ri-shield-check-line text-sm"></i>
            <span className="text-xs">Your messages are secure</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-white min-w-0">
      {/* Chat Header */}
      <div className="h-[72px] flex items-center justify-between px-5 bg-white border-b border-gray-100">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-xl text-gray-600"></i>
          </button>
          
          {isGroup ? (
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setShowGroupMembers(!showGroupMembers)}
            >
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white shadow-sm">
                <i className={`${conversation.group_icon || 'ri-group-line'} text-lg`}></i>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-gray-900">{conversation.group_name || 'Group Chat'}</h3>
                <p className="text-xs text-gray-500">
                  {conversation.participants.slice(0, 3).map(p => 
                    p.user_id === user?.id ? 'You' : p.user_profile?.username?.split(' ')[0]
                  ).join(', ')}
                  {conversation.participants.length > 3 && `, +${conversation.participants.length - 3}`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 font-semibold overflow-hidden shadow-sm">
                  {other?.user_profile?.avatar_url ? (
                    <img src={other.user_profile.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                  ) : (
                    other?.user_profile?.username?.[0]?.toUpperCase() || 'U'
                  )}
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white rounded-full"></div>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-gray-900">{other?.user_profile?.username || 'Unknown User'}</h3>
                <p className="text-xs text-emerald-500 font-medium">Online</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
            <i className="ri-vidicon-line text-xl text-gray-500"></i>
          </button>
          <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
            <i className="ri-phone-line text-xl text-gray-500"></i>
          </button>
          <button className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer">
            <i className="ri-search-line text-xl text-gray-500"></i>
          </button>
          <div className="relative" ref={optionsRef}>
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-more-2-fill text-xl text-gray-500"></i>
            </button>
            {showOptions && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                {isGroup ? (
                  <>
                    <button
                      onClick={() => {
                        setShowOptions(false)
                        setShowGroupMembers(true)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <i className="ri-information-line text-gray-400"></i>
                      Group info
                    </button>
                    <button
                      onClick={() => {
                        setShowOptions(false)
                        setShowInviteMembers(true)
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <i className="ri-user-add-line text-gray-400"></i>
                      Add participants
                    </button>
                    {isCurrentUserAdmin && (
                      <button
                        onClick={() => {
                          setShowOptions(false)
                          setShowGroupSettings(true)
                        }}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        <i className="ri-settings-3-line text-gray-400"></i>
                        Group settings
                      </button>
                    )}
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        setShowOptions(false)
                        onLeaveGroup?.()
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                    >
                      <i className="ri-logout-box-line text-red-400"></i>
                      Exit group
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setShowOptions(false)
                        if (other?.user_id) {
                          window.REACT_APP_NAVIGATE?.(`/user/${other.user_id}`)
                        }
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                    >
                      <i className="ri-user-line text-gray-400"></i>
                      View profile
                    </button>
                    <div className="border-t border-gray-100 my-1"></div>
                    <button
                      onClick={() => {
                        setShowOptions(false)
                        onDeleteConversation()
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                    >
                      <i className="ri-delete-bin-line text-red-400"></i>
                      Delete chat
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Group Members Panel */}
      {showGroupMembers && isGroup && (
        <div className="border-b border-gray-100 bg-gray-50 px-5 py-3 animate-fadeIn">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {conversation.participants.length} participants
            </h4>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowGroupMembers(false)
                  setShowInviteMembers(true)
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-xs font-medium hover:shadow-md transition-all cursor-pointer"
              >
                <i className="ri-user-add-line text-sm"></i>
                Add
              </button>
              <button
                onClick={() => setShowGroupMembers(false)}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500 text-sm"></i>
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {conversation.participants.map((participant) => {
              const isParticipantAdmin = participant.is_admin
              const canRemove = isCurrentUserAdmin && participant.user_id !== user?.id && !isParticipantAdmin
              const isRemoving = removingMember === participant.user_id

              return (
                <div
                  key={participant.user_id}
                  className="flex items-center gap-2 px-2.5 py-1.5 bg-white rounded-lg border border-gray-200 group shadow-sm"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 text-[10px] font-semibold overflow-hidden">
                    {participant.user_profile?.avatar_url ? (
                      <img src={participant.user_profile.avatar_url} alt="" className="w-full h-full object-cover object-top" />
                    ) : (
                      participant.user_profile?.username?.[0]?.toUpperCase() || 'U'
                    )}
                  </div>
                  <span className="text-xs text-gray-700 font-medium">
                    {participant.user_id === user?.id ? 'You' : participant.user_profile?.username}
                  </span>
                  {isParticipantAdmin && (
                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-[9px] font-semibold rounded">
                      Admin
                    </span>
                  )}
                  {canRemove && (
                    <button
                      onClick={async () => {
                        if (onRemoveMember) {
                          setRemovingMember(participant.user_id)
                          try {
                            await onRemoveMember(participant.user_id)
                          } finally {
                            setRemovingMember(null)
                          }
                        }
                      }}
                      disabled={isRemoving}
                      className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 text-red-500 opacity-0 group-hover:opacity-100 hover:bg-red-200 transition-all cursor-pointer ml-1"
                    >
                      {isRemoving ? (
                        <i className="ri-loader-4-line animate-spin text-[10px]"></i>
                      ) : (
                        <i className="ri-close-line text-xs"></i>
                      )}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Messages Area */}
      <div 
        className="flex-1 overflow-y-auto px-5 py-4 space-y-1"
        style={{
          backgroundColor: '#FAFAFA',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E5E7EB' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      >
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="bg-white rounded-xl px-5 py-3 shadow-sm border border-gray-100">
              <p className="text-xs text-gray-500 flex items-center gap-2">
                <i className="ri-shield-check-line text-purple-500"></i>
                Messages are secure. Start the conversation!
              </p>
            </div>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isOwn = message.sender_id === user?.id
              const showDate = shouldShowDateSeparator(index)
              const showSender = shouldShowSenderInfo(index)
              const showAvatar = !isOwn && isGroup && (
                index === messages.length - 1 ||
                messages[index + 1]?.sender_id !== message.sender_id ||
                shouldShowDateSeparator(index + 1)
              )
              const reactions = messageReactions[message.id] || []
              const replyInfo = messageReplies[message.id]
              const senderName = getSenderName(message.sender_id)
              const senderAvatar = getSenderAvatar(message.sender_id)
              const isPinned = isMessagePinned(message.id)

              return (
                <div key={message.id} id={`message-${message.id}`} data-message-id={message.id} className="transition-colors duration-500 rounded-lg">
                  {showDate && (
                    <div className="flex items-center justify-center my-4">
                      <span className="px-4 py-1.5 bg-white rounded-full text-[11px] text-gray-500 font-medium shadow-sm border border-gray-100">
                        {formatDateSeparator(message.created_at)}
                      </span>
                    </div>
                  )}
                  
                  <div 
                    className={`flex items-end gap-2 mb-1 ${isOwn ? 'justify-end' : 'justify-start'}`}
                    onMouseEnter={() => setHoveredMessageId(message.id)}
                    onMouseLeave={() => setHoveredMessageId(null)}
                  >
                    {/* Avatar for group chats */}
                    {!isOwn && isGroup && (
                      <div className="w-7 h-7 flex-shrink-0">
                        {showAvatar && (
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-gray-600 text-[10px] font-semibold overflow-hidden shadow-sm">
                            {senderAvatar ? (
                              <img src={senderAvatar} alt="" className="w-full h-full object-cover object-top" />
                            ) : (
                              senderName?.[0]?.toUpperCase() || 'U'
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Message Actions */}
                    {hoveredMessageId === message.id && !isOwn && (
                      <div className="flex items-center gap-1 animate-fadeIn">
                        <button
                          onClick={() => handleReply(message)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-reply-line text-gray-500 text-sm"></i>
                        </button>
                      </div>
                    )}

                    {(() => {
                      const decoded = decodeSharedContent(message.content)
                      if (decoded.isShared && decoded.content) {
                        return (
                          <div className={`max-w-[65%] relative group`}>
                            {replyInfo && (
                              <QuotedMessage
                                content={replyInfo.content}
                                senderName={replyInfo.sender_name}
                                isOwn={isOwn}
                                onClick={() => scrollToMessage(replyInfo.id)}
                              />
                            )}
                            
                            {decoded.text && (
                              <div
                                className={`px-4 py-2.5 text-sm leading-relaxed mb-1 shadow-sm ${
                                  isOwn
                                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl rounded-tr-md'
                                    : 'bg-white text-gray-800 rounded-2xl rounded-tl-md border border-gray-100'
                                }`}
                              >
                                {isGroup && showSender && !isOwn && (
                                  <p className="text-xs font-semibold text-pink-500 mb-1">{senderName}</p>
                                )}
                                <p className="whitespace-pre-wrap break-words">{decoded.text}</p>
                              </div>
                            )}
                            <SharedContentCard data={decoded.content} isOwn={isOwn} />
                            <p className={`text-[11px] mt-1 flex items-center gap-1 ${isOwn ? 'justify-end text-gray-400' : 'text-gray-400'}`}>
                              {formatMessageTime(message.created_at)}
                              {isOwn && <i className="ri-check-double-line text-purple-500"></i>}
                            </p>
                            <MessageReactionsDisplay 
                              reactions={reactions} 
                              isOwn={isOwn}
                              onReactionClick={(emoji) => handleReaction(message.id, emoji)}
                            />
                          </div>
                        )
                      }

                      return (
                        <div className="relative group max-w-[65%]">
                          {/* Reaction picker */}
                          {showReactionPicker === message.id && (
                            <div className={`absolute bottom-full mb-2 z-50 ${isOwn ? 'right-0' : 'left-0'}`}>
                              <ReactionPicker onSelect={(emoji) => handleReaction(message.id, emoji)} />
                            </div>
                          )}

                          <div
                            className={`relative shadow-sm ${
                              isOwn
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl rounded-tr-md'
                                : 'bg-white rounded-2xl rounded-tl-md border border-gray-100'
                            } overflow-hidden`}
                          >
                            {replyInfo && (
                              <div className="px-2 pt-2">
                                <QuotedMessage
                                  content={replyInfo.content}
                                  senderName={replyInfo.sender_name}
                                  isOwn={isOwn}
                                  onClick={() => scrollToMessage(replyInfo.id)}
                                />
                              </div>
                            )}
                            
                            <div className="px-4 py-2.5">
                              {isGroup && showSender && !isOwn && (
                                <p className="text-xs font-semibold text-pink-500 mb-1">{senderName}</p>
                              )}
                              <div className="flex items-end gap-3">
                                <p className={`whitespace-pre-wrap break-words text-[14px] leading-relaxed ${
                                  isOwn ? 'text-white' : 'text-gray-800'
                                }`}>
                                  {message.content}
                                </p>
                                <span className={`text-[11px] flex items-center gap-1 flex-shrink-0 ${
                                  isOwn ? 'text-white/70' : 'text-gray-400'
                                }`}>
                                  {formatMessageTime(message.created_at)}
                                  {isOwn && <i className="ri-check-double-line text-white/90"></i>}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <MessageReactionsDisplay 
                            reactions={reactions} 
                            isOwn={isOwn}
                            onReactionClick={(emoji) => handleReaction(message.id, emoji)}
                          />
                        </div>
                      )
                    })()}

                    {/* Message Actions - Own messages */}
                    {hoveredMessageId === message.id && isOwn && (
                      <div className="flex items-center gap-1 animate-fadeIn">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowReactionPicker(showReactionPicker === message.id ? null : message.id)
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-emotion-happy-line text-gray-500 text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleReply(message)}
                          className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-reply-line text-gray-500 text-sm"></i>
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowMessageMenu(showMessageMenu === message.id ? null : message.id)
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-more-2-fill text-gray-500 text-sm"></i>
                          </button>
                          {showMessageMenu === message.id && (
                            <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 animate-fadeIn">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(message.content)
                                  setShowMessageMenu(null)
                                }}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                              >
                                <i className="ri-file-copy-line text-gray-400"></i>
                                Copy
                              </button>
                              <button
                                onClick={() => handleDeleteMessage(message.id)}
                                disabled={deletingMessageId === message.id}
                                className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:bg-red-50 cursor-pointer"
                              >
                                {deletingMessageId === message.id ? (
                                  <i className="ri-loader-4-line animate-spin text-red-400"></i>
                                ) : (
                                  <i className="ri-delete-bin-line text-red-400"></i>
                                )}
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="px-5 py-4 bg-white border-t border-gray-100">
        {replyingTo && (
          <ReplyPreview
            message={replyingTo}
            senderName={getSenderName(replyingTo.sender_id)}
            onCancel={cancelReply}
          />
        )}
        
        <form onSubmit={handleSend} className="flex items-end gap-3">
          <div className="relative">
            {showEmojiPicker && (
              <EmojiPicker
                onSelect={handleEmojiSelect}
                onClose={() => setShowEmojiPicker(false)}
              />
            )}
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                showEmojiPicker ? 'bg-purple-100 text-purple-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
              }`}
            >
              <i className="ri-emotion-happy-line text-xl"></i>
            </button>
          </div>
          
          <button
            type="button"
            className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-attachment-2 text-xl"></i>
          </button>
          
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-3 bg-gray-100 rounded-2xl text-[14px] text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:bg-white focus:border focus:border-purple-200 max-h-32 overflow-y-auto transition-all"
              style={{ minHeight: '48px' }}
              disabled={sending}
            />
          </div>
          
          {newMessage.trim() ? (
            <button
              type="submit"
              disabled={sending}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg transition-all cursor-pointer"
            >
              {sending ? (
                <i className="ri-loader-4-line animate-spin text-xl"></i>
              ) : (
                <i className="ri-send-plane-2-fill text-xl"></i>
              )}
            </button>
          ) : (
            <button
              type="button"
              className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-mic-line text-xl"></i>
            </button>
          )}
        </form>
      </div>

      {/* Forward Modal */}
      <ShareToMessageModal
        isOpen={showForwardModal}
        onClose={() => {
          setShowForwardModal(false)
          setForwardContent(null)
        }}
        content={forwardContent}
        onSend={handleForwardToConversation}
        onStartNewConversation={handleForwardToNewConversation}
      />

      {/* Group Settings Modal */}
      {conversation && isGroup && (
        <GroupSettingsModal
          isOpen={showGroupSettings}
          onClose={() => setShowGroupSettings(false)}
          conversation={conversation}
          onUpdateSettings={onUpdateGroupSettings || (async () => {})}
        />
      )}

      {/* Invite Members Modal */}
      {conversation && isGroup && (
        <InviteMembersModal
          isOpen={showInviteMembers}
          onClose={() => setShowInviteMembers(false)}
          conversation={conversation}
          onInviteMembers={onInviteMembers || (async () => {})}
        />
      )}
    </div>
  )
}

export default ChatArea
